import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  ShieldCheck,
  Building2,
  FileCheck2,
  TrendingUp,
  Globe,
  Sparkles,
  Menu,
  X,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  Tooltip,
} from 'recharts';

/* ================= DATA ================= */

const kpis = [
  { label: 'Clients', value: '6,450+', icon: Users },
  { label: 'Team Members', value: '1,280', icon: Users },
  { label: 'Compliance Officers', value: '340', icon: ShieldCheck },
  { label: 'Agencies', value: '95', icon: Building2 },
];

const growth = [
  { m: 'Jan', v: 4200 },
  { m: 'Feb', v: 5400 },
  { m: 'Mar', v: 6100 },
  { m: 'Apr', v: 7900 },
  { m: 'May', v: 9500 },
];

const distribution = [
  { name: 'Corporate', value: 3420 },
  { name: 'Individual', value: 5180 },
  { name: 'Trust', value: 1180 },
];

const steps = [
  { title: 'Onboard', desc: 'Digital onboarding', icon: Users },
  { title: 'Verify', desc: 'AML & KYC checks', icon: FileCheck2 },
  { title: 'Comply', desc: 'Audit-ready records', icon: ShieldCheck },
  { title: 'Monitor', desc: 'Risk tracking', icon: TrendingUp },
];

/* ================= HEADER ================= */

function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* LOGO */}
        <div className="flex items-center gap-3">
          <img src="/Ratified-Logo-Main.svg" alt="logo" className="w-36" />
        </div>

        {/* DESKTOP ACTIONS */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 rounded-xl text-sm font-medium border 
            text-slate-700 hover:bg-slate-50 transition"
          >
            Login
          </button>

          <button
            onClick={() => navigate('/sign-up')}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white
            shadow-md hover:shadow-xl transition-all duration-300
            bg-gradient-to-r from-[#375AE7] to-[#7C8CF8]"
          >
            Sign Up
          </button>
        </div>

        {/* MOBILE */}
        <button
          className="md:hidden p-2 rounded-xl border"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div className="md:hidden px-6 pb-4 space-y-3 bg-white border-t">
          <button className="w-full py-3 rounded-xl border">Login</button>

          <button
            className="w-full py-3 rounded-xl text-white font-medium
            bg-gradient-to-r from-[#375AE7] to-[#7C8CF8]"
          >
            Sign Up
          </button>
        </div>
      )}
    </header>
  );
}

/* ================= MAIN ================= */

export default function LandingPageCompact() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F6F8FC]">
      <Header />

      {/* PAGE WRAPPER */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* ================= KPI ================= */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {kpis.map((k, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border shadow-sm p-5 
              hover:shadow-md transition"
            >
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-indigo-50 text-[#375AE7]">
                  <k.icon size={18} />
                </div>

                <div>
                  <p className="text-2xl font-bold text-slate-900">{k.value}</p>
                  <p className="text-xs text-slate-500">{k.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ================= CHARTS ================= */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* GROWTH */}
          <div className="bg-white rounded-3xl border shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="text-[#375AE7]" />
              <h2 className="font-semibold text-slate-900">Growth Trend</h2>
            </div>

            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={growth}>
                <XAxis dataKey="m" />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke="#375AE7"
                  fill="#EEF2FF"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* DISTRIBUTION */}
          <div className="bg-white rounded-3xl border shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="text-[#375AE7]" />
              <h2 className="font-semibold text-slate-900">Customer Types</h2>
            </div>

            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={distribution}>
                <XAxis dataKey="name" />
                <Tooltip />
                <Bar dataKey="value" fill="#375AE7" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ================= PROCESS ================= */}
        <div className="bg-white rounded-3xl border shadow-sm p-6">
          <h2 className="font-semibold text-slate-900 mb-5">Compliance Flow</h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <div
                key={i}
                className="p-5 rounded-2xl bg-[#F6F8FC] hover:bg-indigo-50 transition"
              >
                <div className="p-2 w-fit rounded-xl bg-white text-[#375AE7] shadow-sm">
                  <s.icon size={18} />
                </div>

                <p className="mt-3 font-semibold text-slate-900">{s.title}</p>
                <p className="text-xs text-slate-500 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ================= FOOTER METRICS ================= */}
        <div className="grid sm:grid-cols-3 gap-5">
          <div className="bg-white rounded-2xl border p-5 text-center">
            <p className="text-3xl font-bold text-[#375AE7]">98%</p>
            <p className="text-xs text-slate-500 mt-1">Compliance Accuracy</p>
          </div>

          <div className="bg-white rounded-2xl border p-5 text-center">
            <p className="text-3xl font-bold text-[#375AE7]">2.4s</p>
            <p className="text-xs text-slate-500 mt-1">Avg Verification Time</p>
          </div>

          <div className="bg-white rounded-2xl border p-5 text-center">
            <p className="text-3xl font-bold text-[#375AE7]">100%</p>
            <p className="text-xs text-slate-500 mt-1">Audit Ready</p>
          </div>
        </div>
      </div>
    </div>
  );
}
